import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api, Load } from '../lib/api'
import { useAuth } from '../lib/auth-context'
import { publishLoadStatusChanged } from '../lib/load-status-sync'

const STATUS_COLORS: Record<string, string> = {
  POSTED: 'bg-blue-100 text-blue-800',
  ASSIGNED: 'bg-purple-100 text-purple-800',
  IN_PROGRESS: 'bg-orange-100 text-orange-800',
  IN_TRANSIT: 'bg-orange-100 text-orange-800',
  DELIVERED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-gray-100 text-gray-600',
}

function AcceptModal({ load, onClose }: { load: Load; onClose: () => void }) {
  const { user } = useAuth()
  const qc = useQueryClient()
  const [truckNumber, setTruckNumber] = useState('TRK-001')
  const [trailerNumber, setTrailerNumber] = useState('TRL-001')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<{ jobNumber: string } | null>(null)
  const carrierPay = typeof load.carrierPayCents === 'number' ? load.carrierPayCents / 100 : (typeof load.totalPayment === 'number' ? load.totalPayment : null)

  const claimMut = useMutation({
    mutationFn: () => api.loads.claim(load.id, {
      carrierOrgId: user?.orgId ?? undefined,
      notes: `Truck ${truckNumber}, trailer ${trailerNumber}`,
    }),
    onSuccess: (res) => {
      setSuccess({ jobNumber: load.loadNumber ?? res.data.jobNumber ?? load.id.slice(0, 8) })
      publishLoadStatusChanged(load.id)
      qc.invalidateQueries({ queryKey: ['carrier-loads'] })
      qc.invalidateQueries({ queryKey: ['carrier-jobs'] })
    },
    onError: (err: any) => {
      setError(err?.response?.data?.error ?? 'Failed to claim load.')
    },
  })

  if (success) {
    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full text-center">
          <div className="text-4xl mb-3">✅</div>
          <h2 className="text-xl font-bold text-gray-900 mb-1">Load Claimed!</h2>
          <p className="text-gray-500 text-sm mb-2">Reference: <span className="font-mono font-semibold">{success.jobNumber}</span></p>
          <p className="text-sm text-gray-500 mb-6">The load is now marked claimed and ready for dispatch assignment.</p>
          <button onClick={onClose} className="w-full bg-blue-700 text-white font-semibold py-2.5 rounded-lg hover:bg-blue-800">Done</button>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full">
        <h2 className="text-lg font-bold text-gray-900 mb-1">Claim Load</h2>
        <p className="text-sm text-gray-500 mb-4">{load.origin} → {load.destination}</p>
        {error && <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded text-sm mb-3">{error}</div>}
        <div className="space-y-3 mb-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Truck Number</label>
            <input value={truckNumber} onChange={e => setTruckNumber(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Trailer Number</label>
            <input value={trailerNumber} onChange={e => setTrailerNumber(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          {carrierPay != null && (
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs text-gray-500">Payout on delivery</div>
              <div className="text-2xl font-bold text-blue-700">${carrierPay.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
              <div className="text-xs text-gray-400 mt-1">QuickPay options available after delivery</div>
            </div>
          )}
        </div>
        <div className="flex gap-3">
          <button onClick={() => claimMut.mutate()} disabled={claimMut.isPending}
            className="flex-1 bg-blue-700 text-white font-semibold py-2.5 rounded-lg hover:bg-blue-800 disabled:opacity-60">
            {claimMut.isPending ? 'Claiming…' : 'Claim Load'}
          </button>
          <button onClick={onClose} className="px-5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Cancel</button>
        </div>
      </div>
    </div>
  )
}

export default function CarrierBoardPage() {
  const [tab, setTab] = useState<'available' | 'my-jobs'>('available')
  const [accepting, setAccepting] = useState<Load | null>(null)

  const { data: postedLoads, isLoading: loadingPosted } = useQuery({
    queryKey: ['carrier-loads'],
    queryFn: async () => { const r = await api.loads.list({ status: 'POSTED', viewer: 'carrier' }); return r.data },
    refetchInterval: 15000,
    enabled: tab === 'available',
  })

  const { data: myJobs, isLoading: loadingJobs } = useQuery({
    queryKey: ['carrier-jobs'],
    queryFn: async () => { const r = await api.jobs.list({ viewer: 'carrier' }); return r.data },
    refetchInterval: 15000,
    enabled: tab === 'my-jobs',
  })

  return (
    <div className="container mx-auto p-6">
      {accepting && <AcceptModal load={accepting} onClose={() => setAccepting(null)} />}

      <div className="flex items-center gap-4 mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Carrier Dashboard</h1>
        <div className="flex bg-gray-100 rounded-lg p-1 gap-1">
          {(['available', 'my-jobs'] as const).map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === t ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
              {t === 'available' ? 'Available Loads' : 'My Jobs'}
            </button>
          ))}
        </div>
      </div>

      {tab === 'available' && (
        <div>
          {loadingPosted && <div className="text-gray-400 text-sm">Loading available loads…</div>}
          {!loadingPosted && (!postedLoads || postedLoads.length === 0) && (
            <div className="bg-white rounded-xl border text-center py-16">
              <p className="text-gray-400">No loads available right now.</p>
              <p className="text-gray-300 text-sm mt-1">Check back soon or refresh the page.</p>
            </div>
          )}
          <div className="space-y-3">
            {postedLoads?.map(load => (
              <div key={load.id} className="bg-white rounded-xl border hover:border-blue-300 transition p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">POSTED</span>
                      {load.loadNumber && <span className="text-xs text-gray-400 font-mono">{load.loadNumber}</span>}
                      <span className="text-xs text-gray-400">{(load.vehicles?.length ?? 0)} vehicle{(load.vehicles?.length ?? 0) !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="font-medium text-gray-900">{load.origin} → {load.destination}</div>
                    {load.vehicles && load.vehicles.length > 0 && (
                      <div className="text-xs text-gray-400 mt-1">
                        {load.vehicles.map(v => [v.year, v.make, v.model].filter(Boolean).join(' ') || v.vin).join(' · ')}
                      </div>
                    )}
                  </div>
                  <div className="text-right ml-4 flex flex-col items-end gap-2">
                    {typeof load.carrierPayCents === 'number' ? (
                      <div className="text-sm font-semibold text-blue-700">
                        Offer: ${(load.carrierPayCents / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500">Offer: TBD</div>
                    )}
                    <button onClick={() => setAccepting(load)}
                      className="bg-green-600 hover:bg-green-700 text-white text-sm font-semibold px-4 py-1.5 rounded-lg transition-colors">
                      Claim Load
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'my-jobs' && (
        <div>
          {loadingJobs && <div className="text-gray-400 text-sm">Loading jobs…</div>}
          {!loadingJobs && (!myJobs || myJobs.length === 0) && (
            <div className="bg-white rounded-xl border text-center py-16">
              <p className="text-gray-400">No active jobs.</p>
              <button onClick={() => setTab('available')} className="text-blue-700 text-sm underline mt-2">Browse available loads</button>
            </div>
          )}
          <div className="space-y-3">
            {myJobs?.map(job => (
              <Link key={job.id} to={`/driver/jobs/${job.id}`}
                className="block bg-white rounded-xl border hover:border-blue-300 transition p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[job.status] ?? 'bg-gray-100 text-gray-600'}`}>
                        {job.status}
                      </span>
                      {job.jobNumber && <span className="text-xs text-gray-400 font-mono">{job.jobNumber}</span>}
                    </div>
                    <div className="text-sm text-gray-600">Job ID: <span className="font-mono">{job.id.slice(0, 8)}</span></div>
                  </div>
                  {typeof job.earnings === 'number' && (
                    <div className="text-right">
                      <div className="text-lg font-bold text-blue-700">${job.earnings.toLocaleString()}</div>
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
